import { useToast } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import actions from "src/actions";
import {
  type CampaignPublishCheckResponse,
  formatPublishUpdateErrorMessage,
  PUBLISH_HEADER_OUTCOME_MS,
  publishCheckResponseFailed,
  type PublishLoadOutcome,
} from "./publishPollingUtils";

export const ADS_CAMPAIGN_PUBLISH_QUERY = "publish_update";

export function adsCampaignPublishSearch(
  publishUpdateId: string | null | undefined
): string {
  const id = normalizeToken(publishUpdateId);
  if (!id) return "";
  return `?${ADS_CAMPAIGN_PUBLISH_QUERY}=${encodeURIComponent(id)}`;
}

function normalizeToken(value: string | null | undefined): string | undefined {
  if (value == null) return undefined;
  const trimmed = String(value).trim();
  return trimmed === "" ? undefined : trimmed;
}

function toCheckUpdateId(executionOrUpdateId: string): string {
  const token = executionOrUpdateId.trim();
  return token.includes(":") ? (token.split(":").pop() ?? token) : token;
}

function removePublishQuery(prev: URLSearchParams): URLSearchParams {
  if (!prev.has(ADS_CAMPAIGN_PUBLISH_QUERY)) return prev;
  const next = new URLSearchParams(prev);
  next.delete(ADS_CAMPAIGN_PUBLISH_QUERY);
  return next;
}

export function useCampaignPublishPolling(options: {
  campaignId: string | undefined;
  campaignExecutionId: string | undefined;
  refetchCampaign: () => Promise<void>;
}): {
  serverPublishStep: string | null;
  headerPublishLockToken: string | undefined;
  /** True while we have an execution/update id to poll (URL or campaign). */
  isPublishPollingActive: boolean;
  /** After URL-driven polling settles; parent merges with managed flow via `mergePublishLoadOutcomes`. */
  urlPublishLoadOutcome: PublishLoadOutcome;
} {
  const { campaignId, campaignExecutionId, refetchCampaign } = options;
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [serverPublishStep, setServerPublishStep] = useState<string | null>(
    null
  );
  const [completedCheckUpdateId, setCompletedCheckUpdateId] = useState<
    string | undefined
  >(undefined);
  const [urlPublishLoadOutcome, setUrlPublishLoadOutcome] =
    useState<PublishLoadOutcome>("idle");
  const celebrateResetRef = useRef<number | undefined>(undefined);

  useEffect(
    () => () => {
      if (celebrateResetRef.current != null) {
        window.clearTimeout(celebrateResetRef.current);
      }
    },
    []
  );

  const queryUpdateId = normalizeToken(
    searchParams.get(ADS_CAMPAIGN_PUBLISH_QUERY)
  );
  const rawLockToken =
    normalizeToken(campaignExecutionId) ?? queryUpdateId;
  const checkUpdateId = rawLockToken
    ? toCheckUpdateId(rawLockToken)
    : undefined;

  useEffect(() => {
    setCompletedCheckUpdateId(undefined);
    // When we strip `?publish_update=` after a terminal poll, `checkUpdateId` becomes
    // undefined — do not clear success/error here or the header never shows.
    if (checkUpdateId != null) {
      setUrlPublishLoadOutcome("idle");
      if (celebrateResetRef.current != null) {
        window.clearTimeout(celebrateResetRef.current);
        celebrateResetRef.current = undefined;
      }
    }
  }, [checkUpdateId]);

  const activeCheckUpdateId =
    checkUpdateId != null && checkUpdateId !== completedCheckUpdateId
      ? checkUpdateId
      : undefined;

  const headerPublishLockToken =
    activeCheckUpdateId != null ? rawLockToken : undefined;

  const isPublishPollingActive = Boolean(campaignId && activeCheckUpdateId);

  useEffect(() => {
    if (!campaignId || !activeCheckUpdateId) {
      setServerPublishStep(null);
      return;
    }

    let cancelled = false;
    let intervalId: number | undefined;

    const stopPolling = () => {
      if (intervalId != null) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }
    };

    const scheduleOutcomeReset = () => {
      if (celebrateResetRef.current != null) {
        window.clearTimeout(celebrateResetRef.current);
      }
      celebrateResetRef.current = window.setTimeout(() => {
        setUrlPublishLoadOutcome("idle");
        celebrateResetRef.current = undefined;
      }, PUBLISH_HEADER_OUTCOME_MS);
    };

    const completeTerminalPoll = async () => {
      stopPolling();
      setServerPublishStep(null);
      setSearchParams((prev) => removePublishQuery(prev), { replace: true });
      setCompletedCheckUpdateId(activeCheckUpdateId);
      try {
        await refetchCampaign();
      } catch {
        // best-effort
      }
    };

    const tick = async () => {
      try {
        const res = (await actions.ADMIN.checkCampaignUpdate(
          campaignId,
          activeCheckUpdateId
        )) as CampaignPublishCheckResponse;
        if (cancelled) return;

        if (res.status === "PROCESSING") {
          setServerPublishStep(normalizeToken(res.update_status) ?? null);
          return;
        }

        const failed = publishCheckResponseFailed(res);
        setUrlPublishLoadOutcome(failed ? "error" : "success");
        if (failed) {
          toast({
            title: "Error publishing campaign",
            description: formatPublishUpdateErrorMessage(res),
            status: "error",
          });
        } else {
          toast({
            title: "Campaign published",
            description: "Campaign published",
            status: "success",
          });
        }
        scheduleOutcomeReset();
        await completeTerminalPoll();
      } catch (err: unknown) {
        if (cancelled) return;
        const message =
          err != null && typeof err === "object" && "message" in err
            ? String((err as { message?: string }).message)
            : "Could not check publish status";
        toast({
          title: "Error publishing campaign",
          description: message,
          status: "error",
        });
        setUrlPublishLoadOutcome("error");
        scheduleOutcomeReset();
        await completeTerminalPoll();
      }
    };

    void tick();
    intervalId = window.setInterval(() => void tick(), 5000);

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [
    campaignId,
    activeCheckUpdateId,
    refetchCampaign,
    setSearchParams,
    toast,
  ]);

  return {
    serverPublishStep,
    headerPublishLockToken,
    isPublishPollingActive,
    urlPublishLoadOutcome,
  };
}
